/* jshint esversion: 6 */
import React from 'react';
import PropTypes from 'prop-types';
import ErrorModule from 'app/mixins/ErrorModule.jsx';
import DropDown from 'app/components/common/DropDown.jsx';
import SearchControl from 'app/components/common/SearchControl.jsx';
import FeedItem from 'app/components/DropDownItems/FeedItem.jsx';
import SocialButtonConnect from 'app/components/common/SocialButtonConnect.jsx';
import * as IconFactory from 'app/components/common/IconFactory.jsx';
import SelectedFeedItem from 'app/components/common/SelectedFeedItem.jsx';
import Loading from 'app/components/common/Loading.jsx';
import FeedSearchResult from 'app/components/CreateFeed/FeedSearchResult.jsx';
import StyledSelect from 'app/components/common/StyledSelect.jsx';
import * as _ from 'underscore';
import * as appConstants from 'app/redux/constants.js';
import { Breakpoint } from 'react-socks';

const errorMap = (errors, errorText, props, key, state) => {
	// Safety check, if errorText isn't a string it won't have a match method so just return and empty string
	// probably in a weird case at this point anyhow and the error being reported isn't fully baked or an actual blocker
	if (!_.isString(errorText)) {
		return '';
	}

	if (errorText.match('Content is an HTML Document, not an RSS/Atom feed')) {
		return "We can't find a feed on this web page (This is an HTML page not a RSS / Atom feed)";
	} else if (errorText.match('Invalid Url (404)')) {
		return "There's no feed at this address (HTTP 404)";
	} else if (errorText.match('This feed is bigger')) {
		let upgradeLink = null;
		if (props.user.detail.pubLevel <= 1) {
			upgradeLink = (<a href="/account/subscription/compare" className="error-link">{"Check out dlvr.it's premium plans."}</a>);
		}
		return (
			<span key={key}>
				<span>{errorText} {upgradeLink}</span>
			</span>
		);
	} else if (errorText.match("We can't find a working feed")) {
		if (errors.suggestedFeeds !== undefined && errors.suggestedFeeds.feeds !== undefined && errors.suggestedFeeds.feeds.length) {
			return (
				<span key={key}>
					{errorText}. <span className="additional-info">Please try another search or choose a related feed below.</span>
				</span>
			);
		}
	} else if (errorText.match("POSSIBLE SOCIAL SEARCH")) {
		return (
			<span key={key} className="additional-info">
				Want to share from a social profile? You can do that <a href="/share/automate/new/inputs?social=true">here</a>
			</span>
		);
	} else if (errorText.match("USER SEARCH")) {
		let hasSuggestedFeeds = false;
		if (props.app.errors.api.length) {
			for (let i = 0; i < props.app.errors.api.length; ++i) {
				let e = props.app.errors.api[i];
				if (e.errors !== undefined &&
					e.errors.suggestedFeeds !== undefined &&
					e.errors.suggestedFeeds.feeds !== undefined &&
					e.errors.suggestedFeeds.feeds.length) {
					hasSuggestedFeeds = true;
				}
			}
		}
		if (hasSuggestedFeeds || props.app.errors.api.length === 1) {
			return (
				<span key={key}>
					<span className="search-echo">"{state.inputContent}"</span> We can’t find a working feed there. <br/><span className="additional-info">Please try another search or choose a related feed below. <br/>Want to share from a social profile? You can do that <a href="/share/automate/new/inputs?social=true">here</a></span>
				</span>
			);
		} else if (props.feeds.searchResults.feeds.length) {
			return (
				<span key={key}>
					<span className="additional-info">Please try another search or choose a related feed below. <br/>Want to share from a social profile? You can do that <a href="/share/automate/new/inputs?social=true">here</a></span>
				</span>
			);
		} else {
			return (
				<span key={key}>
					<span className="additional-info">Want to share from a social profile? You can do that <a href="/share/automate/new/inputs?social=true">here</a></span>
				</span>
			);
		}

	} else if (errorText.match("YOUTUBE")) {
		return (
			<span key={key}>
				<span className="additional-info">Want to share YouTube videos? Select the YouTube option <a href="/share/automate/new/inputs">here</a></span>
			</span>
		);
	}

	return errorText;
};

class CreateFeed extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			inputContent: '',
			searchResults: [],
			currentPage: 1,
			defaultFocus: true,
			searchQuery: '',
			externalFeedUrl: '',
			externalFeedSearch: false,
			selectExternalFeed: false,
			searchLanguage: 'en',
			isSearching: false
		};

		this.triggerExternalSearch = this.triggerExternalSearch.bind(this);
		this.inputHandler = this.inputHandler.bind(this);
		this.focusHandler = this.focusHandler.bind(this);
		this.selectFeed = this.selectFeed.bind(this);
		this.removeFeed = this.removeFeed.bind(this);
		this.searchSuggestion = this.searchSuggestion.bind(this);
		this.updateSearchLanguage = this.updateSearchLanguage.bind(this);
		this.checkForSocialSearch = this.checkForSocialSearch.bind(this);

	}

	componentDidMount() {

		// clean up at mount
		this.props.resetFeedSearch();
		this.props.resetFeedValidation();

		if (this.props.externalFeedUrl !== '') {
			this.setState({
				externalFeedUrl: this.props.externalFeedUrl,
				externalFeedSearch: true
			}, () => {
				this.triggerExternalSearch();
			});
		}

		if (this.props.searchQuery !== '') {
			this.setState({
				searchQuery: this.props.searchQuery
			});
		}

	}

	componentWillUnmount() {

		let onboarding = (typeof(Storage) !== undefined) ? JSON.parse(window.localStorage.getItem('onboarding')) : null;

		if (onboarding !== null) {
			if (onboarding.searchAttempts !== undefined) {
				if (mixpanel !== undefined) {
					mixpanel.track("Onboarding - Feed Search Attempts", {
						'searchAttempts': onboarding.searchAttempts
					})
				}
				if (this.props.recordEvent) {
					this.props.recordEvent({
						pub_id: this.props.user.detail.id,
						event_name: 'Onboarding - Feed Search Attempts',
						event_data: {
							'searchAttempts': onboarding.searchAttempts
						}
					});
				}
			}
		}

		// clear out validation errors on teardown
		this.props.deleteApiError(['INPUT_VALIDATE', 'FEED_SEARCH', 'FEED_VALIDATE']);

	}

	static getDerivedStateFromProps(props, state) {
		let nextState = {};

		if (props.feeds && props.feeds.searchResults.length && state.selectExternalFeed) {
			nextState.selectExternalFeed = false;
			nextState.addExternalFeed = true;
		}

		if (props.user && props.user.perms && props.user.perms.resources.inputsAvailable === 0) {
			nextState.defaultFocus = false;
		}

		return Object.keys(nextState).length ? nextState : null;

	}

	componentDidUpdate(prevProps, prevState, snapshot) {

		if (this.state.addExternalFeed) {
			this.setState({
				addExternalFeed: false,
			});
			if (this.state.searchResults.length) {
				this.selectFeed(this.state.searchResults[0]);
			}
		}

		if (prevProps.app.loading.FEED_SEARCH === true && this.props.app.loading.FEED_SEARCH === false) {
			this.checkForSocialSearch(this.state.inputContent);
			this.setState({
				isSearching: false
			});
		}

	}

	triggerExternalSearch() {

		this.inputHandler(null, this.state.externalFeedUrl);

		this.setState({
			externalFeedSearch: false,
			externalFeedUrl: '',
			selectExternalFeed: true
		});

	}

	checkForSocialSearch(query) {
		let message = null,
			errors = [];

		// fake a feed search error to help redirect people to the
		// input tiles if it looks like they're trying to add a social
		errors = this.props.getApiErrors(['FEED_SEARCH']);
		if (_.isString(query)) {
			if (query.match(/facebook|twitter|instagram/i)) {
				message = "POSSIBLE SOCIAL SEARCH";
			} else if (query.match(/\@[\w+]/)) {
				message = "USER SEARCH";
			} else if (query.match(/youtube/i)) {
				message = "YOUTUBE";
			}

			if (message) {
				this.props.setApiError({
					type: 'FEED_SEARCH',
					error: {
						errors: {
							validation: {
								query: message
							}
						}
					}
				});
			}
		}
	}

	inputHandler(event, newValue){
		let searchResults = [],
			onboarding = (typeof(Storage) !== undefined) ? JSON.parse(window.localStorage.getItem('onboarding')) : null;

		if (this.props.canAddFeeds === false) {
			if (newValue === '') {
				this.props.resetFeedSearch();
				if (this.props.removeInvalidFeeds) {
					this.props.removeInvalidFeeds();
				}
			}
			return;
		}

		// debounce (kinda) search requests - prevent a new request from firing if it's the same
		if (this.state.isSearching && newValue === this.state.inputContent) {
			return;
		}

		newValue = newValue.trim();

		// for mobile devices that like to capitalize the first letter - which would blow up a direct feed request
		// We cannot use .toLowerCase() because that modifies the entire URL and blows up many, many searches
		if (newValue.match(/^http/i)) {
			newValue = newValue.replace(/^http/i, 'http');
		}

		if (newValue !== '') {
			this.props.deleteApiError(['FEED_VALIDATE', 'INPUT_VALIDATE']);
			this.props.feedSearch(newValue, this.state.searchLanguage);

			if (onboarding !== null) {
				onboarding.searchAttempts++;
				window.localStorage.setItem('onboarding', JSON.stringify(onboarding));
			}

			if (this.props.onboarding !== null) {
				dataLayer.push({
					'event': 'onboarding-trigger-search',
					'search-term': newValue
				});
				if (mixpanel !== undefined) {
					mixpanel.track("Onboarding - Trigger Search", {
						'term': newValue
					})
				}

				if (this.props.recordEvent) {
					this.props.recordEvent({
						pub_id: this.props.user.detail.id,
						event_name: 'Onboarding - Trigger Search',
						event_data: {
							'term': newValue
						}
					});
				}
			}
		} else if (newValue === '') {
			this.props.deleteApiError(['FEED_VALIDATE', 'INPUT_VALIDATE', 'FEED_SEARCH']);
			this.props.resetFeedSearch();
			// Feed search is being reset remove any invalid feeds
			if (this.props.removeInvalidFeeds) {
				this.props.removeInvalidFeeds();
			}
		}

		this.setState({
			inputContent: newValue,
			currentPage: 1,
			searchQuery: '',
			isSearching: true
		});
	}

	focusHandler(e) {

		let trigger = (this.props.context === 'curate') ? 'curate' : 'automate';

		if (this.props.user.perms.resources.inputsAvailable === 0) {
			e.target.blur();
			this.props.showUpgradeModal(this.props.closeAllModals, 'inputs', null, trigger + '_create');
		} else {
			if (dataLayer) {
				if (this.props.onboarding !== null) {
					if (_.findWhere(dataLayer, {'event': 'onboarding-start-search'}) === undefined) {
						dataLayer.push({
							'event': 'onboarding-start-search'
						});
					}
				}
			}
		}

	}

	selectFeed(feed){
		this.setState({
			inputContent: '',
			searchResults: []
		});

		feed.type = this.props.app.config.constants.INPUT_TYPE_FEED;
		feed.name = feed.title;

		this.props.resetFeedSearch();

		this.props.onFeedSelect(feed);

		let onboarding = (typeof(Storage) !== undefined) ? JSON.parse(window.localStorage.getItem('onboarding')) : null;

		if (onboarding !== null) {
			if (onboarding.searchAttempts !== undefined) {
				if (mixpanel !== undefined) {
					mixpanel.track("Onboarding - Feed Selected", {
						'feed': feed
					})
				}
				if (this.props.recordEvent) {
					this.props.recordEvent({
						pub_id: this.props.user.detail.id,
						event_name: 'Onboarding - Feed Selected',
						event_data: {
							'feed': feed
						}
					});
				}
			}

		}

	}

	removeFeed(event) {
		let feedId = event.currentTarget.dataset.feedid;
		this.props.deleteApiError([appConstants.FEED_SEARCH, appConstants.FEED_VALIDATE]);
		this.props.onFeedRemoval(feedId);
	}

	searchSuggestion(e) {
		let suggestion = null,
			onboarding = (typeof(Storage) !== undefined) ? JSON.parse(window.localStorage.getItem('onboarding')) : null;

		if (e) {
			e.preventDefault();
		}

		if (e.currentTarget.dataset.tag !== undefined) {
			suggestion = e.currentTarget.dataset.tag;
			window.scrollTo(0, 0);
			if (mixpanel && onboarding) {
				mixpanel.track("Topic Search Clicked", {
					topic: suggestion
				});
			}

			if (this.props.recordEvent && onboarding) {
				this.props.recordEvent({
					pub_id: this.props.user.detail.id,
					event_name: 'Topic Search Clicked',
					event_data: {
						topic: suggestion
					}
				});
			}
		}

		if (suggestion) {
			this.setState({
				searchQuery: suggestion
			});
		}

	}

	updateSearchLanguage(e) {
		let onboarding = (typeof(Storage) !== undefined) ? JSON.parse(window.localStorage.getItem('onboarding')) : null;

		if (mixpanel && onboarding) {
			mixpanel.track('Search Language Changed', {
				locale: e.currentTarget.value
			});
		}

		if (this.props.recordEvent && onboarding) {
			this.props.recordEvent({
				pub_id: this.props.user.detail.id,
				event_name: 'Search Language Changed',
				event_data: {
					locale: e.currentTarget.value
				}
			});
		}

		this.setState({
			searchLanguage: e.currentTarget.value,
			isSearching: false
		}, function(){
			this.inputHandler(null, this.state.inputContent);
		});

	}


	render() {
		let searchResults = null,
			selectedFeeds = null,
			feedLabel = '',
			constants = this.props.app.config.constants,
			editButton = null,
			loading = null,
			pagerControl = null,
			currentResults = [],
			searchError = null,
			assetsUrl = this.props.app.config.assetsUrl,
			suggestion = null,
			searchQuery = this.state.searchQuery,
			facebookButton = null,
			twitterButton = null,
			twistoutCopy = '',
			twistoutButton = null,
			socialButtons = null,
			languageOptions = [
				{value: 'en', label: 'English'},
				{value: 'es', label: 'Español'},
				{value: 'fr', label: 'Français'},
				{value: 'it', label: 'Italiano'},
				{value: 'ja', label: '日本語'},
				{value: 'pt', label: 'Português'},
				{value: 'de', label: 'Deutsch'},
				{value: 'nl', label: 'Nederlands'},
				{value: 'ru', label: 'Русский'},
				{value: 'zh', label: '中文'}
			],
			socialInputs = null,
			searchWrapperClass = 'feed',
			languageClass = 'language-select',
			searchTags = null,
			resultList = [],
			titleCopy = 'Post from RSS feed, site, or blog.',
			placeholderCopy = 'Enter site, topic, or RSS feed URL',
			languageSelector = null,
			imgPath = this.props.app.config.assetsUrl + "/" + this.props.app.config.imageUrl;


		if (this.props.inputType === 'wordpress') {
			titleCopy = 'Auto post items from a WordPress site';
			placeholderCopy = 'Enter your WordPress site';
			searchWrapperClass += " wordpress";
		} else {
			languageSelector = (
				<StyledSelect
					options={languageOptions}
					value={this.state.searchLanguage}
					onChange={this.updateSearchLanguage}
					selectClass={languageClass}/>
			);
		}

		searchError = (
			<ErrorModule
				app={this.props.app}
				keys={[appConstants.FEED_SEARCH, appConstants.FEED_VALIDATE, appConstants.INPUT_VALIDATE, appConstants.ROUTE_CREATE, appConstants.CURATION_ROUTE_CREATE]}
				render={(state) => {
					if (state.errors.length) {
						//error.errors.suggestedFeeds.query
						return (
							<div className="feed-search-error">
								{state.errors.map((error, i) => {
									if (error.errors === undefined || error.errors.validation === undefined) {
										return null;
									}
									return (
										<div key={"feed_error_" + i}>
											{_.values(error.errors.validation).map((errorText, i) => {
												let key = "error_text_" + i;
												if (error.errors.suggestedFeeds !== undefined && error.errors.suggestedFeeds.query !== undefined) {
													return (
														<span key={key}>
															<span className="search-echo">"{error.errors.suggestedFeeds.query}"</span> {errorMap(error.errors, errorText, this.props, key, this.state)}
														</span>
													);
												} else {
													return errorMap(error.errors, errorText, this.props, key, this.state);
												}
											})}
										</div>
									);
								})}
							</div>
						);
					}
					return null;
				}}/>
		);

		if (this.props.initialFeeds.length) {
			feedLabel = (this.props.initialFeeds.length > 1) ? 'Feeds' : 'Feed';
			selectedFeeds = (
				<div>
					<p className="editor-section-heading">Selected {feedLabel}</p>
					<ul className="dlvr-feed-list">
						{this.props.initialFeeds.map(function(feed, i){
							let isCurated = (this.props.context === 'curate') ? true : false;

							return (
								<SelectedFeedItem
									{...this.props}
									key={i}
									feed={feed}
									onAdvancedFeedConfigure={this.props.onAdvancedFeedConfigure}
									removeFeed={this.removeFeed}
									isCurated={isCurated} />
							);
						}, this)}
					</ul>
				</div>
			);
		}

		if (this.props.app.loading.FEED_SEARCH) {
			searchResults = (
				<Loading label="Searching"/>
			);
		} else {
			if (this.props.feeds.searchResults.tags.length) {
				searchTags = (
					<div className="related-tags">
						<h3>Related searches</h3>
						<ul className="search-tags-list clearfix">
							{this.props.feeds.searchResults.tags.map((tag, i) => {
								return (
									<li key={i} className="search-tag" onClick={this.searchSuggestion} data-tag={tag} role="button" aria-label="search by tag">
										{tag}
									</li>
								);
							})}
						</ul>
					</div>
				);
			}

			let searchResultList = [].concat(this.props.feeds.searchResults.feeds);

			if (this.props.searchErrors) {
				if (this.props.searchErrors.errors && this.props.searchErrors.errors.suggestedFeeds !== undefined) {
					searchResultList = searchResultList.concat(this.props.searchErrors.errors.suggestedFeeds.feeds);
				}
			}

			if (searchResultList.length) {
				searchResults = (
					<div className="search-results">
						<ul className="search-results-list">
							{searchResultList.map((result, i) => {
								return (
									<FeedSearchResult
										key={i}
										result={result}
					 					selectFeed={this.selectFeed}/>
								);
							})}
						</ul>
						{searchTags}
					</div>
				);
			}
		}


		if (this.props.app.flags.routeViewType == constants.ROUTE_VIEW_ADVANCED || this.props.context === 'curate') {
			

			/*
			
			*/

			socialInputs = (
				<div className="input-connect-buttons">
					<div className="social-section-header">
						Or reshare Posts, Reels, and Stories from a profile or page.
					</div>
					<div className="tile-wrapper x-small complex">
						<SocialButtonConnect
							{...this.props}
							beforeClick={this.props.onSocialClick}
							service={constants.INPUT_TYPE_INSTAGRAM_BUSINESS}
							icon={`${imgPath}svg-logos/instagram-purp.svg`}
							recordPath={this.props.recordPath}
							router={this.props.router}
							serviceLabel={"Connect Instagram"}
							context="automate" />
						<SocialButtonConnect
							{...this.props}
							beforeClick={this.props.onSocialClick}
							service={constants.INPUT_TYPE_FACEBOOK_PAGE}
							icon={`${imgPath}svg-logos/facebook.svg`}
							recordPath={this.props.recordPath}
							router={this.props.router}
							serviceLabel={"Connect Facebook"}
							context="automate" />
					</div>
				</div>
			);
			
		}

		let focusHandler = null;
		if (this.props.user.detail.routeViewType === constants.ROUTE_VIEW_ADVANCED) {
			focusHandler = this.focusHandler;
		}

		let searchControl = (
			<div>
				<Breakpoint desktop only>
					<div className="search-controls">
						<SearchControl
							{...this.props}
							extraClasses="text-input feed"
							placeholder={placeholderCopy}
							wrapperClass={searchWrapperClass}
							fieldIcon="icon ion-search"
							changeHandler={this.inputHandler}
							runOnFocus={false}
							focusHandler={focusHandler}
							defaultFocus={this.state.defaultFocus}
							fieldValue={this.state.searchQuery}
							trackReset={true}
							buttonClass="search-button"
							buttonLabel={<i className="icon ion-search"></i>} />
						{languageSelector}
					</div>
				</Breakpoint>
				<Breakpoint mobile only>
					<div className="search-controls">
						<SearchControl
							{...this.props}
							extraClasses="text-input feed"
							placeholder={placeholderCopy}
							wrapperClass={searchWrapperClass}
							fieldIcon="icon ion-search"
							changeHandler={this.inputHandler}
							runOnFocus={false}
							focusHandler={focusHandler}
							defaultFocus={this.state.defaultFocus}
							fieldValue={this.state.searchQuery}
							trackReset={true}
							buttonClass="search-button"
							buttonLabel={<i className="icon ion-search"></i>} />
					</div>
				</Breakpoint>
			</div>
		);

		// hide the search control for simple if they have a feed set up
		if (this.props.app.flags.routeViewType == constants.ROUTE_VIEW_SIMPLE && this.props.initialFeeds.length >= 1) {
			searchControl = null;
		}

		return(
			<div>
				<div className="feed-search">
					<div className="editor-section-heading">
						<div className="section-header">
							{titleCopy}
						</div>
					</div>
					{searchControl}
					{(this.props.initialFeeds.length) ? null : searchError}
					{suggestion}
					{socialInputs}
					{selectedFeeds}
					{searchResults}
				</div>
			</div>
		);
	}
};

CreateFeed.displayName = "CreateFeed";

CreateFeed.propTypes = {
	onFeedSelect: PropTypes.func,
	onFeedRemoval: PropTypes.func,
	initialFeeds: PropTypes.array,
	canAddFeeds: PropTypes.bool,
	onAdvancedFeedConfigure: PropTypes.func,
	onSocialClick: PropTypes.func,
	onPinterestClick: PropTypes.func,
	recordPath: PropTypes.func,
	context: PropTypes.string,
	searchErrors: PropTypes.oneOfType([
		PropTypes.object,
		PropTypes.array,
		PropTypes.string
	]),
	externalFeedUrl: PropTypes.string
},

CreateFeed.defaultProps = {
	onFeedSelect: () => {},
	onFeedRemoval: () => {},
	initialFeeds: [],
	canAddFeeds: true,
	onAdvancedFeedConfigure: () => {},
	onSocialClick: () => {},
	onPinterestClick: () => {},
	recordPath: null,
	context: '',
	searchErrors: null,
	externalFeedUrl: ''
};

export default CreateFeed;
